import { CallMerge, Person, TrendingUp } from '@mui/icons-material';
import { On3IconAthleteVerified } from '@on3/icons';
import CircleCheck from '@on3/icons/src/components/CircleCheck';
import { Link } from '@on3/ui-lib/components/Link';
import { SocialIcon } from '@on3/ui-lib/components/SocialIcon';
import { On3Recruit } from '@on3/ui-lib/components/Svg/On3Recruit';
import { recruitsSubConfig } from '@on3/ui-lib/utils/constants';
import { useDynamicRoute } from '@on3/ui-lib/utils/hooks/useDynamicRoute';
import { SportIcon } from 'components/Icons/SportIcon';
import { ImageSlider } from 'components/UI/ImageSlider';

import styles from './JoinPage.module.scss';

const sports = [
  {
    name: 'Football',
    slug: 'football',
    icon: <SportIcon sportName="Football" />,
  },
  {
    name: "Men's Basketball",
    slug: 'basketball',
    icon: <SportIcon sportName="Basketball" />,
  },
  {
    name: "Women's Basketball",
    slug: 'womens-basketball',
    icon: <SportIcon sportName="Basketball" />,
  },
  {
    name: 'Softball',
    slug: 'softball',
    icon: <SportIcon sportName="Softball" />,
  },
  {
    name: 'More Sports',
    icon: (
      <div className={styles.icons}>
        <SportIcon sportName="Baseball" />
        <SportIcon sportName="Volleyball" />
        <SportIcon sportName="Soccer" />
        <SportIcon sportName="Golf" />
        <SportIcon sportName="Swimming" />
      </div>
    ),
  },
];

export const JoinPage = () => {
  const { formatHref } = useDynamicRoute();

  return (
    <div className={styles.block}>
      <section className={styles.hero}>
        <div className={styles.inner}>
          <div>
            <On3Recruit
              className={styles.logo}
              fill="#A2A9AD"
              fillAlt="#fff"
              isAlt
            />
            <h1>
              Get a Profile. <br />
              Be Recruited.
            </h1>
          </div>
          <Link className={styles.btn} href="/recruits/search/">
            Athletes & Parents Start Here
          </Link>
        </div>
        <img
          alt="Five-star recruit puts on hat at signing day event"
          className={styles.background}
          src={`https://on3static.com/cdn-cgi/image/width=1440,quality=0.8/static/on3/get-recruited-hero.jpg`}
        />
      </section>
      <section className={styles.benefits}>
        <div className={styles.inner}>
          <div className={styles.sports}>
            <div>
              {sports.map((sport, idx) => (
                <Link
                  className={styles.sport}
                  href={formatHref({
                    path: '/recruits/search/',
                    query: {
                      sport: sport.slug,
                    },
                  })}
                  key={idx}
                >
                  {sport.icon}
                  <span>{sport.name}</span>
                </Link>
              ))}
            </div>
          </div>
          <div className={styles.table}>
            <header className={styles.header}>
              <h2>On3+ Recruits Benefits</h2>
              <div>
                <span className="sr-only">Price: </span>
                <div className={styles.pricing}>
                  <span>${recruitsSubConfig.pricing.base}</span>
                  <span>
                    ${recruitsSubConfig.pricing.offer}/
                    {recruitsSubConfig.pricing.period}
                  </span>
                  <span>{recruitsSubConfig.pricing.discount} off</span>
                </div>
              </div>
            </header>
            <ul>
              {[
                'Get a Verified Profile',
                'Showcase Highlights and Stats',
                'Private Recruiting Dashboard',
                'Coaching Contact Directory',
                'Access NIL Valuations',
                'Scouting and Ranking Opportunity ',
                'Free On3 National Subscription',
              ].map((item, idx) => (
                <li className={styles.benefit} key={idx}>
                  <Link href="/recruits/search/">
                    <span>
                      {idx === 0 && <On3IconAthleteVerified />}
                      {item}
                    </span>
                    <CircleCheck aria-hidden={true} />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
      <section>
        <div className={styles.inner}>
          <h2 className={styles.heading}>Where Talent Meets Opportunity</h2>
        </div>
        <ImageSlider
          className={styles.images}
          images={[
            {
              url: 'https://on3static.com/cdn-cgi/image/height=620,width=393,quality=0.8,fit=cover/static/on3/get-recruited-sample-1-2.png?v=3',
              alt: 'Sample of recruiting profile',
            },
            {
              url: 'https://on3static.com/cdn-cgi/image/height=620,width=393,quality=0.8,fit=cover/static/on3/get-recruited-sample-2-2.png?v=3',
              alt: 'Sample of recruiting profile',
            },
            {
              url: 'https://on3static.com/cdn-cgi/image/height=620,width=393,quality=0.8,fit=cover/static/on3/get-recruited-sample-3-2.png?v=3',
              alt: 'Sample of recruiting profile',
            },
            {
              url: 'https://on3static.com/cdn-cgi/image/height=620,width=393,quality=0.8,fit=cover/static/on3/get-recruited-sample-4-2.png?v=3',
              alt: 'Sample of recruiting profile',
            },
            {
              url: 'https://on3static.com/cdn-cgi/image/height=620,width=393,quality=0.8,fit=cover/static/on3/get-recruited-sample-5-2.png?v=3',
              alt: 'Sample of recruiting profile',
            },
            {
              url: 'https://on3static.com/cdn-cgi/image/height=620,width=393,quality=0.8,fit=cover/static/on3/get-recruited-sample-6-2.png?v=3',
              alt: 'Sample of recruiting profile',
            },
            {
              url: 'https://on3static.com/cdn-cgi/image/height=620,width=393,quality=0.8,fit=cover/static/on3/get-recruited-sample-7-2.png?v=3',
              alt: 'Sample of recruiting profile',
            },
            {
              url: 'https://on3static.com/cdn-cgi/image/height=620,width=393,quality=0.8,fit=cover/static/on3/get-recruited-sample-8-2.png?v=3',
              alt: 'Sample of recruiting profile',
            },
          ]}
        />
      </section>
      <section className={styles.kpi}>
        <div className={styles.inner}>
          <h2 className={styles.heading}>The Leader in Recruiting</h2>
          <div className={styles.cards}>
            {[
              {
                icons: [<Person key="user" />],
                title: '50,000',
                description: 'Number of On3 Profiles',
              },
              {
                icons: [<CallMerge className={styles.traffic} key="traffic" />],
                title: '35 Million',
                description: 'On3 users per month',
              },
              {
                icons: [
                  <SocialIcon
                    className={styles.social}
                    key="instagram"
                    type="instagram"
                  />,
                  <SocialIcon
                    className={styles.social}
                    key="tiktok"
                    type="tiktok"
                  />,
                  <SocialIcon className={styles.social} key="x" type="x" />,
                ],
                title: '5.2 Million',
                description: 'Social Media Followers to On3',
              },
              {
                icons: [<TrendingUp key="trending" />],
                title: '125,000',
                description: 'New Members',
              },
            ].map((item, idx) => (
              <div className={styles.card} key={idx}>
                <div className={styles.icons}>
                  {item.icons.map((icon, i) => (
                    <div className={styles.icon} key={i}>
                      {icon}
                    </div>
                  ))}
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section>
        <div className={styles.inner}>
          <h2 className={styles.heading}>The Athlete Community</h2>
        </div>
        <ImageSlider
          className={styles.images}
          images={[
            {
              url: 'https://on3static.com/cdn-cgi/image/height=620,width=393,quality=0.8,fit=cover/static/on3/get-recruited-example-1-2.png?v=2',
              alt: 'Example of recruiting profile',
            },
            {
              url: 'https://on3static.com/cdn-cgi/image/height=620,width=393,quality=0.8,fit=cover/static/on3/get-recruited-example-2-2.png?v=2',
              alt: 'Example of recruiting profile',
            },
            {
              url: 'https://on3static.com/cdn-cgi/image/height=620,width=393,quality=0.8,fit=cover/static/on3/get-recruited-example-3-2.png?v=2',
              alt: 'Example of recruiting profile',
            },
            {
              url: 'https://on3static.com/cdn-cgi/image/height=620,width=393,quality=0.8,fit=cover/static/on3/get-recruited-example-4-2.png?v=2',
              alt: 'Example of recruiting profile',
            },
            {
              url: 'https://on3static.com/cdn-cgi/image/height=620,width=393,quality=0.8,fit=cover/static/on3/get-recruited-example-5-2.png?v=2',
              alt: 'Example of recruiting profile',
            },
          ]}
        />
      </section>
    </div>
  );
};
